import React, { useEffect, useRef, useState } from "react";

/* NotificationBell — badge + dropdown feed, driven by useRealtime.
 * Live connection dot, unread count badge, click a notification to mark read
 * (and optionally navigate to the order). Pure presentational; state comes in
 * as props so it can sit in any workspace header. */

const agoOf = (ts) => {
  const t = new Date(ts).getTime();
  if (!t || isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell({ notifications, unread, connected, onMarkRead, onMarkAllRead, onOpen }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button" title="Notifications" onClick={() => setOpen((v) => !v)}
        style={{ position: "relative", background: "transparent", border: "none", cursor: "pointer",
          padding: 6, borderRadius: 8, display: "inline-flex", color: "inherit" }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span style={{ position: "absolute", top: 0, right: 0, minWidth: 16, height: 16, padding: "0 4px",
            borderRadius: 8, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        <span title={connected ? "Live" : "Reconnecting…"} style={{ position: "absolute", bottom: 3, right: 3,
          width: 7, height: 7, borderRadius: 4, background: connected ? "#10b981" : "#9ca3af", boxShadow: "0 0 0 1.5px var(--bg-surface,#fff)" }} />
      </button>

      {open && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 340, maxHeight: 440, overflowY: "auto",
          background: "var(--bg-surface,#fff)", color: "var(--text-primary,#18181b)", border: "1px solid var(--chrome-border,#e5e7eb)",
          borderRadius: 12, boxShadow: "0 20px 50px rgba(0,0,0,0.22)", zIndex: 9000 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid var(--chrome-border,#eee)" }}>
            <strong style={{ fontSize: 13 }}>Notifications</strong>
            {unread > 0 && <button type="button" onClick={onMarkAllRead}
              style={{ background: "transparent", border: "none", color: "#2563eb", fontSize: 12, cursor: "pointer" }}>Mark all read</button>}
          </div>
          {(!notifications || notifications.length === 0) && (
            <div style={{ padding: 22, textAlign: "center", color: "var(--text-tertiary,#9ca3af)", fontSize: 12 }}>No notifications yet.</div>
          )}
          {(notifications || []).map((n) => (
            <div key={n.id} onClick={() => { if (!n.read) onMarkRead(n.id); if (onOpen && n.entity_id) onOpen(n); setOpen(false); }}
              style={{ display: "flex", gap: 10, padding: "11px 14px", cursor: "pointer",
                background: n.read ? "transparent" : "rgba(37,99,235,0.06)", borderBottom: "1px solid var(--chrome-border,#f3f4f6)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, marginTop: 5, flexShrink: 0, background: n.read ? "transparent" : "#2563eb" }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>{n.title}</div>
                {n.body && <div style={{ fontSize: 11.5, color: "var(--text-secondary,#6b7280)", lineHeight: 1.35, marginTop: 1 }}>{n.body}</div>}
                <div style={{ fontSize: 10.5, color: "var(--text-tertiary,#9ca3af)", marginTop: 3 }}>{agoOf(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
